import type { SubscriptionDetails } from "../types/client.types";
import type { MonitoringType } from "@/constants/monitoring";
import { SUBSCRIPTION_PLANS } from "@/constants/plans";

/** Shape used to prefill selected plans in edit mode; matches SelectedPlan from useClientPlans. */
export interface InitialSelectedPlan {
  plan_id: string;
  monitoringType: MonitoringType;
  planTitle: string;
  price: string;
  platforms: string[];
  included?: string[];
}

/**
 * Maps an API subscription to the corresponding monitoring type using plan_name and entity_types.
 * Used to prefill edit-mode tabs from client overview data.
 */
export function getMonitoringTypeFromSubscription(
  sub: SubscriptionDetails,
): MonitoringType {
  const name = (sub.plan_name ?? "").toLowerCase();
  const entities = sub.entity_types ?? [];

  if (name.includes("pulse") || entities.includes("pulse")) return "pulse";
  if (
    name.includes("influencer") ||
    (entities.includes("influencers") && !name.includes("keyword"))
  )
    return "influencer";
  // Explicit keyword plan names (e.g. "Enterprise - Keyword Monitoring") must be checked before source,
  // since keyword plans can also have entity_types ["social_media", "influencers", "posts", "articles"] and would otherwise match the source (articles && posts) condition.
  if (name.includes("keyword")) return "keyword";
  // Legacy/DB uses "quadmedia" for source; entity_types like ["quadmedia", "articles", "posts"] match source.
  if (
    name.includes("quadmedia") ||
    name.includes("source") ||
    name.includes("usage based") ||
    entities.includes("quadmedia") ||
    entities.includes("source") ||
    (entities.includes("articles") && entities.includes("posts"))
  )
    return "source";
  if (
    entities.some((e) => ["social_media", "posts", "articles"].includes(e)) &&
    !name.includes("influencer")
  )
    return "keyword";

  return "keyword";
}

/**
 * Builds plan title and price for display from plan_id (constants) or subscription plan_name.
 */
function getPlanDisplay(
  planId: string,
  planName: string,
): { title: string; price: string } {
  const plan = SUBSCRIPTION_PLANS[planId];
  if (plan) {
    const title = plan.tier.charAt(0).toUpperCase() + plan.tier.slice(1);
    return {
      title,
      price: `$${plan.base_price}/mo`,
    };
  }
  return {
    title: planName || "Current plan",
    price: "—",
  };
}

/**
 * Derives initial selectedPlans and tabDisabled from API subscriptions.
 * Each monitoring type with at least one matching subscription is active and prefilled;
 * types with no subscription are inactive.
 */
export function getInitialPlansStateFromSubscriptions(
  subscriptions: SubscriptionDetails[],
): {
  selectedPlans: Map<string, InitialSelectedPlan>;
  tabDisabled: Record<MonitoringType, boolean>;
  expandedIds: Set<string>;
} {
  const selectedPlans = new Map<string, InitialSelectedPlan>();
  const tabDisabled: Record<MonitoringType, boolean> = {
    keyword: true,
    influencer: true,
    source: true,
    pulse: true,
  };
  const expandedIds = new Set<string>();

  for (const sub of subscriptions) {
    const monitoringType = getMonitoringTypeFromSubscription(sub);
    tabDisabled[monitoringType] = false;

    const alreadyHasType = [...selectedPlans.keys()].some((k) =>
      k.startsWith(`${monitoringType}-`),
    );
    if (alreadyHasType) continue;

    const { title, price } = getPlanDisplay(sub.plan_id, sub.plan_name);
    const planKey = `${monitoringType}-${title}`;
    expandedIds.add(planKey);
    selectedPlans.set(planKey, {
      plan_id: sub.plan_id,
      monitoringType,
      planTitle: title,
      price,
      platforms: [...(sub.platforms ?? [])],
    });
  }

  return { selectedPlans, tabDisabled, expandedIds };
}

/**
 * Returns which monitoring types have at least one subscription (active).
 */
export function getActiveMonitoringTypes(
  subscriptions: SubscriptionDetails[],
): Set<MonitoringType> {
  const active = new Set<MonitoringType>();
  for (const sub of subscriptions) {
    active.add(getMonitoringTypeFromSubscription(sub));
  }
  return active;
}
